import type { AppEnvironment } from '../types/App.types';
import { ConfigV2GlobalResourcesS3BucketType, ConfigV2PackageType } from '../types/ConfigV2.types';

export const Constants = {
  BLUFIN_INDEX_FILE: '.blufin/src/index.ts'
};

export const WORKSPACE_MODULES = 'modules' as const;
export const WORKSPACE_SERVICES = 'services' as const;
export const WORKSPACE_APPS = 'apps' as const;
export const WORKSPACE_SDKS = 'sdks' as const;
export const WORKSPACE_SITES = 'sites' as const;
export const WORKSPACE_PLUGINS = 'plugins' as const;
export const WORKSPACE_MOBILE = 'mobile' as const;

/**
 * Top-level workspace directory names in the monorepo.
 * These are the primary folders that organize different types of NPM packages.
 */
export const WORKSPACE_FOLDERS = [
  WORKSPACE_APPS,
  WORKSPACE_MOBILE,
  WORKSPACE_MODULES,
  WORKSPACE_PLUGINS,
  WORKSPACE_SDKS,
  WORKSPACE_SERVICES,
  WORKSPACE_SITES
] as const;

/** SDK subdirectory for DynamoDB table SDKs */
export const SDK_TYPE_DYNAMODB = 'dynamodb' as const;

/** SDK subdirectory for MySQL database SDKs */
export const SDK_TYPE_MYSQL = 'mysql' as const;

/** SDK subdirectory for PostgreSQL database SDKs */
export const SDK_TYPE_POSTGRES = 'postgres' as const;

/** SDK subdirectory for IoT client/server SDKs */
export const SDK_TYPE_IOT = 'iot' as const;

/** SDK subdirectory for service client SDKs */
export const SDK_TYPE_SERVICES = 'services' as const;

/**
 * Directories to check when scanning for packages in the repository.
 * Includes subdirectories for SDK packages (dynamodb, mysql, postgres, services).
 */
export const PACKAGE_DIRECTORIES = [
  WORKSPACE_MODULES,
  WORKSPACE_SERVICES,
  `${WORKSPACE_SDKS}/${SDK_TYPE_SERVICES}`,
  `${WORKSPACE_SDKS}/${SDK_TYPE_DYNAMODB}`,
  `${WORKSPACE_SDKS}/${SDK_TYPE_IOT}`
] as const;

/**
 * The identity used by scheduled handlers (and other trusted infra) when stamping audit fields
 * (eg: audit.actor / createdBy / resolvedBy). Scheduled handlers run as the System principal — there is
 * no JWT/user. Typed as a literal via `as const` so `typeof SYSTEM_ACTOR_ID` resolves to `'system'`.
 */
export const SYSTEM_ACTOR_ID = 'system' as const;

/** The build version string used for localhost development builds. */
export const LOCALHOST_BUILD_ENVIRONMENT: AppEnvironment = 'localhost' as const;
/**
 * The version prefix stamped on every locally-built artifact. Used bare (`0.0.0-local`) by sites,
 * services, Storybook and the runtime environment clients; Electron builds append a timestamp token
 * via `LocalBuildVersion` in `@blufin/core`.
 */
export const LOCAL_BUILD_VERSION_PREFIX: string = '0.0.0-local' as const;

/**
 * Valid deployment environments supported by the platform.
 * Used for validation across React, Electron, and config validation.
 */
export const VALID_ENVIRONMENTS: readonly AppEnvironment[] = ['localhost', 'dev', 'stg', 'prod'] as const;

/**
 * The web icon filenames that vary by environment, as root-absolute URI paths because the Vite dev middleware
 * compares them against a request URI. The generator emits one production file plus a `localhost`-badged
 * variant for each; deployed environments serve the production file. Declared here so `@blufin/scripts` and
 * `@blufin/config` read one definition without a dependency cycle.
 */
export const WEB_ENV_VARIANT_PATHS = ['/apple-touch-icon.png', '/favicon.ico'] as const;

/** S3 Bucket types that are used for static websites. */
export const STATIC_WEBSITE_S3_TYPES = [
  ConfigV2GlobalResourcesS3BucketType.ELECTRON,
  ConfigV2GlobalResourcesS3BucketType.SPA,
  ConfigV2GlobalResourcesS3BucketType.STATIC_SITE,
  ConfigV2GlobalResourcesS3BucketType.STORYBOOK
];

/** Key used for S3 version lookups when no packageName is associated (eg: Storybook). */
export const VERSIONS_ROOT_KEY = '__root__' as const;

/** ---- Package Type Groups ---- */

const {
  APP_REACT,
  APP_REACT_NATIVE,
  APP_ELECTRON_BUNDLED,
  APP_ELECTRON_WEB,
  MODULE_NODE,
  MODULE_NODE_BIN,
  MODULE_BROWSER,
  MODULE_REACT_NATIVE,
  MODULE_UI,
  SITE_NEXTJS,
  SDK_DYNAMODB,
  SDK_IOT_CLIENT,
  SDK_IOT_SERVER,
  SDK_MYSQL,
  SDK_POSTGRES,
  SDK_SERVICE,
  SERVICE
} = ConfigV2PackageType;

/** App package types (applications that run in browsers or as Electron apps) */
export const APP_TYPES = [APP_REACT, APP_REACT_NATIVE, APP_ELECTRON_BUNDLED, APP_ELECTRON_WEB] as const;

/** Electron app package types (subset of APP_TYPES) */
export const ELECTRON_APP_TYPES = [APP_ELECTRON_BUNDLED, APP_ELECTRON_WEB] as const;

/** Module package types (libraries that can be imported by other packages, plus published Node executables) */
export const MODULE_TYPES = [MODULE_NODE, MODULE_NODE_BIN, MODULE_BROWSER, MODULE_REACT_NATIVE, MODULE_UI] as const;

/** Site package types (static sites built with Next.js) */
export const SITE_TYPES = [SITE_NEXTJS] as const;

/** Service package types (AWS Lambda microservices) */
export const SERVICE_TYPES = [SERVICE] as const;

/** Database SDK types (auto-generated database clients) */
export const DATABASE_SDK_TYPES = [SDK_DYNAMODB, SDK_MYSQL, SDK_POSTGRES] as const;

/** IoT SDK types (auto-generated IoT client/server SDKs) */
export const IOT_SDK_TYPES = [SDK_IOT_CLIENT, SDK_IOT_SERVER] as const;

/** All SDK types (database SDKs + service SDKs + IoT SDKs) */
export const ALL_SDK_TYPES = [
  SDK_DYNAMODB,
  SDK_IOT_CLIENT,
  SDK_IOT_SERVER,
  SDK_MYSQL,
  SDK_POSTGRES,
  SDK_SERVICE
] as const;

/** Package types eligible for S3 deployment */
export const S3_ELIGIBLE_TYPES = [SITE_NEXTJS, APP_REACT, APP_ELECTRON_BUNDLED, APP_ELECTRON_WEB] as const;

/** Package types that can be published to npm (modules + service SDKs) */
export const PUBLISHABLE_TYPES = [...MODULE_TYPES, SDK_SERVICE] as const;

/** Package types that can be deployed to API Gateway */
export const API_GATEWAY_TYPES = [SERVICE] as const;

/** All package types (for validation and completeness checks) */
export const ALL_PACKAGE_TYPES = Object.values(ConfigV2PackageType) as ConfigV2PackageType[];

/** ---- Internal API Service Package Names ---- */

/**
 * Auth service package name used by CLI tools for API authentication.
 * This is a core service also defined in CdkCoreService enum.
 */
export const AUTH_SERVICE_PACKAGE = '@blufin/auth-service' as const;

/**
 * AWS Infrastructure service package name used by CLI tools for deployment operations.
 * This service provides infrastructure management endpoints (not a core service).
 */
export const AWS_INFRA_SERVICE_PACKAGE = '@blufin/aws-infra-service' as const;
